import React from 'react';
import {
  DownloadSimpleIcon,
  PauseIcon,
  PlayIcon,
  ShareFatIcon,
} from '@phosphor-icons/react';
import {
  SocialIcon,
  SOCIAL_LABELS,
} from 'volto-ministerio-publico-federal/components/Icons/social';
import { MPFIconButton } from 'volto-ministerio-publico-federal/components/UI';
import { EditItemButton } from 'volto-ministerio-publico-federal/components/View/CapaView/EditItemButton';
import { useFormattedDate } from 'volto-ministerio-publico-federal/hooks/useFormattedDate';
import { useSharePage } from 'volto-ministerio-publico-federal/hooks/useSharePage';

import { formatTempo, type EpisodioCard } from './helpers';
import { useAudioPlayer } from './useAudioPlayer';

interface RadioCardProps {
  card: EpisodioCard;
  onEditItem?: (index: number) => void;
}

/**
 * Card de um episódio: marca da plataforma, data, chamada e o player.
 *
 * O áudio é servido pelo próprio portal (ver `audioSrc` em `helpers.ts`), não
 * por um embed da plataforma — é o que mantém os três cards com a mesma altura
 * e o mesmo desenho, coisa que os iframes de Spotify e Deezer não permitem.
 */
export const RadioCard: React.FC<RadioCardProps> = ({ card, onEditItem }) => {
  const { formatShortDate, formatShortTime } = useFormattedDate();
  const { audioRef, tocando, atual, duracao, alternar, buscar } =
    useAudioPlayer();
  const compartilhar = useSharePage(card.titulo, card.link || undefined);

  const nome = card.titulo || 'este episódio';
  const marca = card.plataforma ? SOCIAL_LABELS[card.plataforma] : undefined;

  return (
    <article className="mpf-podcast__card">
      {/* `preload="metadata"`: só o cabeçalho do arquivo, o suficiente para a
          duração aparecer na barra sem baixar o áudio de quem não vai ouvir.

          `media-has-caption` desligado porque não há legenda a apontar: o
          bloco não coleta transcrição do episódio. Um `<track>` sem arquivo
          satisfaria a regra e não ajudaria ninguém. A saída de verdade é um
          campo de transcrição no schema — enquanto ele não existe, esta é uma
          lacuna conhecida de acessibilidade, não um descuido. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} src={card.audio} preload="metadata" />

      <header className="mpf-podcast__topo">
        {card.plataforma &&
          (card.link ? (
            <a
              href={card.link}
              className="mpf-podcast__marca"
              target="_blank"
              rel="noopener noreferrer"
            >
              <SocialIcon network={card.plataforma} variant="brand" size={32} />
              <span className="sr-only">
                {`Ouvir no ${marca} (abre em nova aba)`}
              </span>
            </a>
          ) : (
            <span className="mpf-podcast__marca">
              <SocialIcon
                network={card.plataforma}
                variant="brand"
                size={32}
                label={marca}
              />
            </span>
          ))}

        {card.data && (
          <time className="mpf-podcast__data" dateTime={card.data}>
            {formatShortDate(card.data)}
            <span className="mpf-podcast__separador" aria-hidden="true">
              •
            </span>
            {formatShortTime(card.data)}
          </time>
        )}

        {onEditItem && (
          <EditItemButton
            label={`Editar episódio: ${card.titulo || 'sem título'}`}
            onClick={() => onEditItem(card.sourceIndex)}
          />
        )}
      </header>

      {card.titulo && <h3 className="mpf-podcast__titulo">{card.titulo}</h3>}
      {card.descricao && (
        <p className="mpf-podcast__descricao">{card.descricao}</p>
      )}

      {/* `input[type=range]` e não uma div pintada: dá busca por mouse E por
          teclado (setas, Home/End) e o papel de slider sem nenhum ARIA na mão.
          `aria-valuetext` porque o valor cru em segundos não é anunciável. */}
      <input
        type="range"
        className="mpf-podcast__progresso"
        min={0}
        max={duracao || 0}
        step={1}
        value={Math.min(atual, duracao || 0)}
        disabled={!duracao}
        onChange={(event) => buscar(Number(event.target.value))}
        aria-label={`Posição de ${nome}`}
        aria-valuetext={`${formatTempo(atual)} de ${formatTempo(duracao)}`}
        style={
          {
            '--mpf-podcast-progresso': `${
              duracao ? (atual / duracao) * 100 : 0
            }%`,
          } as React.CSSProperties
        }
      />

      <footer className="mpf-podcast__acoes">
        <MPFIconButton
          size="m"
          variant="primary"
          icon={
            tocando ? <PauseIcon weight="fill" /> : <PlayIcon weight="fill" />
          }
          label={tocando ? `Pausar ${nome}` : `Tocar ${nome}`}
          onClick={alternar}
        />

        <span className="mpf-podcast__tempo">
          {formatTempo(atual)}
          <span aria-hidden="true"> / </span>
          <span className="sr-only">de</span>
          {formatTempo(duracao)}
        </span>

        <MPFIconButton
          as="a"
          href={card.audio}
          download
          size="m"
          variant="ghost"
          icon={<DownloadSimpleIcon />}
          label={`Baixar ${nome}`}
        />

        <MPFIconButton
          size="m"
          variant="ghost"
          icon={<ShareFatIcon />}
          label={`Compartilhar ${nome}`}
          onClick={compartilhar}
        />
      </footer>
    </article>
  );
};

export default RadioCard;
